import { create } from 'zustand';
import { transactionService } from '../services/transactionService';
import { Transaction } from '../types';
import { handleApiError, formatErrorForDisplay } from '../lib/errorHandler';
import { addBreadcrumb, setChargingContext } from '../lib/sentry';

/**
 * Active charging session data
 */
export interface ActiveSessionData {
  transactionId: string;
  chargePointId: string;
  connectorId: string;
  locationName?: string;
  chargePointName?: string;
  batteryLevel: number;
  currentPower: number;
  energyConsumed: number;
  startedAt: Date;
}

interface ActiveChargingState {
  // State
  hasActiveSession: boolean;
  transactionId: string | null;
  chargePointId: string | null;
  connectorId: string | null;
  locationName: string | null;
  chargePointName: string | null;
  batteryLevel: number;
  currentPower: number;
  energyConsumed: number;
  startedAt: Date | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  setActiveSession: (data: ActiveSessionData) => void;
  updateMeterValues: (soc?: number, power?: number, energy?: number) => void;
  clearActiveSession: () => void;
  fetchActiveSession: () => Promise<void>;
}

/**
 * Map Transaction API response to ActiveSessionData
 */
function mapTransactionToSessionData(transaction: Transaction): ActiveSessionData {
  return {
    transactionId: transaction.id,
    chargePointId: transaction.chargePointId,
    connectorId: transaction.connectorId,
    locationName: transaction.chargePoint?.location?.name,
    chargePointName: transaction.chargePoint?.name,
    batteryLevel: transaction.currentSoc ?? transaction.currentBattery ?? 0,
    currentPower: transaction.currentPowerKw ?? 0,
    energyConsumed: transaction.totalKwh ?? transaction.energyUsed ?? 0,
    startedAt: new Date(transaction.startConfirmedByEvseAt ?? transaction.startRequestedAt ?? transaction.createdAt),
  };
}

/**
 * Active Charging Session Store
 *
 * Manages global state for the currently active charging session.
 * This is used by the BottomNavCharging component to show real-time
 * charging status in the navigation bar.
 */
export const useActiveChargingStore = create<ActiveChargingState>()((set, get) => ({
  // Initial state
  hasActiveSession: false,
  transactionId: null,
  chargePointId: null,
  connectorId: null,
  locationName: null,
  chargePointName: null,
  batteryLevel: 0,
  currentPower: 0,
  energyConsumed: 0,
  startedAt: null,
  isLoading: false,
  error: null,

  /**
   * Set the active charging session with full data
   */
  setActiveSession: (data: ActiveSessionData) => {
    console.log('[ActiveChargingStore] Setting active session:', data.transactionId);
    addBreadcrumb('charging', 'Active session set', { transactionId: data.transactionId });
    setChargingContext({ transactionId: data.transactionId, chargePointId: data.chargePointId, connectorId: data.connectorId });
    set({
      hasActiveSession: true,
      transactionId: data.transactionId,
      chargePointId: data.chargePointId,
      connectorId: data.connectorId,
      locationName: data.locationName ?? null,
      chargePointName: data.chargePointName ?? null,
      batteryLevel: data.batteryLevel,
      currentPower: data.currentPower,
      energyConsumed: data.energyConsumed,
      startedAt: data.startedAt,
      error: null,
    });
  },

  /**
   * Update meter values from polling
   */
  updateMeterValues: (soc?: number, power?: number, energy?: number) => {
    const { hasActiveSession, transactionId } = get();
    if (!hasActiveSession) {
      console.warn('[ActiveChargingStore] updateMeterValues called without active session');
      return;
    }
    console.log('[ActiveChargingStore] Updating meter values for', transactionId, { soc, power, energy });

    const nextState: Partial<ActiveChargingState> = {};

    if (Number.isFinite(soc)) {
      nextState.batteryLevel = Number(soc);
    }
    if (Number.isFinite(power)) {
      nextState.currentPower = Number(power);
    }
    if (Number.isFinite(energy)) {
      nextState.energyConsumed = Number(energy);
    }

    if (Object.keys(nextState).length === 0) {
      return;
    }

    set(nextState);
  },

  /**
   * Clear the active session (called when charging stops)
   */
  clearActiveSession: () => {
    console.log('[ActiveChargingStore] Clearing active session');
    addBreadcrumb('charging', 'Session cleared');
    setChargingContext(null);
    set({
      hasActiveSession: false,
      transactionId: null,
      chargePointId: null,
      connectorId: null,
      locationName: null,
      chargePointName: null,
      batteryLevel: 0,
      currentPower: 0,
      energyConsumed: 0,
      startedAt: null,
      error: null,
    });
  },

  /**
   * Fetch active session from backend API
   * Called on app startup to restore active session state
   */
  fetchActiveSession: async () => {
    const { isLoading } = get();
    if (isLoading) {
      console.log('[ActiveChargingStore] Already fetching, skipping');
      return;
    }

    console.log('[ActiveChargingStore] Fetching active session from API');
    set({ isLoading: true, error: null });

    try {
      const activeTransactions = await transactionService.getActiveTransactions();

      if (activeTransactions.length > 0) {
        // Use the first active transaction (typically there should only be one)
        const activeTransaction = activeTransactions[0];
        console.log('[ActiveChargingStore] Found active transaction:', activeTransaction.id);

        const sessionData = mapTransactionToSessionData(activeTransaction);
        set({
          hasActiveSession: true,
          transactionId: sessionData.transactionId,
          chargePointId: sessionData.chargePointId,
          connectorId: sessionData.connectorId,
          locationName: sessionData.locationName ?? null,
          chargePointName: sessionData.chargePointName ?? null,
          batteryLevel: sessionData.batteryLevel,
          currentPower: sessionData.currentPower,
          energyConsumed: sessionData.energyConsumed,
          startedAt: sessionData.startedAt,
          isLoading: false,
          error: null,
        });
      } else {
        console.log('[ActiveChargingStore] No active transactions found');
        set({
          hasActiveSession: false,
          transactionId: null,
          chargePointId: null,
          connectorId: null,
          locationName: null,
          chargePointName: null,
          batteryLevel: 0,
          currentPower: 0,
          energyConsumed: 0,
          startedAt: null,
          isLoading: false,
          error: null,
        });
      }
    } catch (error) {
      console.error('[ActiveChargingStore] Failed to fetch active session:', error);
      const appError = handleApiError(error);
      set({
        isLoading: false,
        error: formatErrorForDisplay(appError),
      });
    }
  },
}));
